import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  category: string;
  cover_image_url: string | null;
  published: boolean;
  author_name: string;
  created_at: string;
  updated_at: string;
}

export interface BlogPostInput {
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  category: string;
  cover_image_url?: string | null;
  published?: boolean;
  author_name?: string;
}

export const BLOG_CATEGORIES = [
  { value: "devocional", label: "📝 Devocionais para Músicos" },
  { value: "dicas", label: "🎶 Dicas para Tocar Melhor" },
  { value: "reflexao", label: "🙏 Reflexões Cristãs" },
] as const;

export const useBlogPosts = (onlyPublished = true) => {
  return useQuery({
    queryKey: ["blog-posts", onlyPublished],
    queryFn: async () => {
      let query = (supabase as any)
        .from("blog_posts")
        .select("*")
        .order("created_at", { ascending: false });

      if (onlyPublished) {
        query = query.eq("published", true);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as BlogPost[];
    },
  });
};

export const useBlogPost = (slug: string | undefined) => {
  return useQuery({
    queryKey: ["blog-post", slug],
    queryFn: async () => {
      if (!slug) return null;
      const { data, error } = await (supabase as any)
        .from("blog_posts")
        .select("*")
        .eq("slug", slug)
        .maybeSingle();
      if (error) throw error;
      return data as BlogPost | null;
    },
    enabled: !!slug,
  });
};

export const useCreateBlogPost = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (post: BlogPostInput) => {
      const { data, error } = await (supabase as any)
        .from("blog_posts")
        .insert(post)
        .select()
        .single();
      if (error) throw error;
      return data as BlogPost;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["blog-posts"] });
    },
  });
};

export const useUpdateBlogPost = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...post }: { id: string } & Partial<BlogPostInput>) => {
      const { data, error } = await (supabase as any)
        .from("blog_posts")
        .update(post)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as BlogPost;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["blog-posts"] });
    },
  });
};

export const useDeleteBlogPost = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any).from("blog_posts").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["blog-posts"] });
    },
  });
};
